import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TherapistStateService } from '@therapist/shared/services/state/therapist-state.service';
import { ExerciseStateService } from '@therapist/shared/services/state/exercise-state.service';
import { TherapyPlanStateService } from '@therapist/shared/services/state/therapy-plan-state.service';
import { TherapistClientStateService } from '@therapist/shared/services/state/therapist-client-state.service';
import { catchError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TherapistAuthStateService {

  readonly #router = inject(Router);

  readonly #clientStateService = inject(TherapistClientStateService);
  readonly #exerciseStateService = inject(ExerciseStateService);
  readonly #therapistStateService = inject(TherapistStateService);
  readonly #therapyPlanStateService = inject(TherapyPlanStateService);

  readonly #accessToken = signal<string | null>(null);

  readonly #JWT_TOKEN_NAME = 'therapist-token-v1';

  readonly hasValidToken = computed(() => {
    const token = this.#accessToken();

    if (!token) {
      return false;
    }

    console.log('token is expired: ', this.isTokenExpired(token));
    return !this.isTokenExpired(token);
  });

  /**
   * Checks if there is a valid token in local storage, loads the profile of the user and navigates to the home page.
   *
   * @return {Promise<void>} A promise that resolves when the app is initialized.
   *
   */
  initializeAppAndProfile(): Promise<void> {
    // Password reset and register pages, no need to check for token or load profile
    if (window.location.href.includes('password-request-reset')
      || window.location.href.includes('password-reset')
      || window.location.href.includes('register')) {
      return Promise.resolve();
    }

    const token = localStorage.getItem(this.#JWT_TOKEN_NAME);

    if (token && !this.isTokenExpired(token)) {
      this.setAccessToken(token);

      // Wartet, bis das Profil geladen wurde
      return new Promise(resolve => {
        this.#therapistStateService.loadTherapistProfile$()
          .pipe(catchError(error => {
            console.warn('Error while loading therapist profile: ', error.status);

            /**
             if (error.status === 401 || error.status === 403) {
             this.logout();
             resolve();
             }
             **/

            // ToDo: add special behavior for every other issue than 401 or 403 (Sanactivo is currently under maintenance...)
            this.logout();
            resolve();
            return [];
          }))
          .subscribe({
            next: () => resolve(),
            error: () => {
              // Bei Fehler trotzdem auflösen, und ggf. weiter zum Login
              this.logout();
              resolve();
            }
          });
      });
    }

    console.warn('No valid token found in initializeAppAndProfile, navigating to login');
    this.#router.navigate(['/login']);
    return Promise.resolve();
  }

  /**
   * Checks if the token is expired
   *
   * @param token
   */
  isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tokenExpire = payload.exp;

      const currentTime = new Date().getTime() / 1000;
      return currentTime > tokenExpire;
    } catch {
      return false;
    }
  }

  /**
   * Retrieves the current access token as a readonly signal.
   *
   * @return {Signal<string | null>} A signal containing the access token, which may be a string or null.
   */
  getAccessToken(): Signal<string | null> {
    return this.#accessToken.asReadonly();
  }

  /**
   * Sets the access token and stores it in local storage.
   *
   * @param {string | null} token - The access token to be set. Pass null to clear the token.
   * @return {void}
   */
  setAccessToken(token: string | null) {
    this.#accessToken.set(token);
    localStorage.setItem(this.#JWT_TOKEN_NAME, token!);
  }

  /**
   * Logs the user out by removing all authentication-related data and navigating to the login page.
   *
   * @return {void} Does not return a value.
   */
  logout(): void {
    this.#clientStateService.removeAll();
    this.#exerciseStateService.removeAll();
    this.#therapistStateService.removeAll();
    this.#therapyPlanStateService.removeAll();

    this.removeAll();

    this.#router.navigate(['/login']);
  }

  /**
   * Removes all authentication-related data.
   * Clears the access token and removes the stored token from local storage.
   *
   * @return {void} Does not return a value.
   */
  removeAll() {
    this.setAccessToken(null);
    localStorage.removeItem(this.#JWT_TOKEN_NAME);
  }
}
